import { cn } from '@goaltree/ui/lib/utils'
import type { ReactNode } from 'react'

// Where Simli's live frame (1080×1920, Trinity-2) places the coach's 768×768
// portrait, measured by matching a live frame against the portrait: the square
// is 129% of the frame width, centred, starting 6.5% down. Showing the portrait
// at that spot means the face doesn't move when the live video fades in.
const SQUARE = { left: '-14.58%', top: '6.51%', width: '129.17%' }

// The portrait laid out like the live frame, inside a box that covers its
// parent the way the video's object-cover does (the parent needs `container-type: size`
// and a size). A blurred copy fills what the square doesn't reach.
export function CoachStillFrame({ image, className }: { image: string; className?: string }) {
  return (
    <div className={cn('absolute inset-0 overflow-hidden', className)}>
      <img
        src={image}
        alt=""
        className="absolute inset-0 size-full scale-125 object-cover object-bottom blur-2xl brightness-75"
      />
      <div className="absolute top-1/2 left-1/2 h-[max(100cqh,calc(100cqw*16/9))] w-[max(100cqw,calc(100cqh*9/16))] -translate-1/2">
        <img
          src={image}
          alt=""
          // Fades out at the bottom so the square melts into the fill instead of ending in a line.
          className="absolute aspect-square max-w-none [mask-image:linear-gradient(to_bottom,black_80%,transparent)]"
          style={SQUARE}
        />
      </div>
    </div>
  )
}

// A round face (bottom nav, PiP) showing the live video, or the portrait laid out
// the same way until it's live: the frame's top band, zoomed a little, so the
// whole head fits with room above.
export function CoachCircleFrame({
  image,
  live,
  children,
}: {
  image?: string
  live: boolean
  children?: ReactNode
}) {
  return (
    <span className="absolute inset-0 origin-center scale-125 [&_video]:object-[center_12%]">
      {image && (
        // The portrait's square within that same top band of the frame.
        <img
          src={image}
          alt=""
          className={cn(
            'absolute aspect-square max-w-none transition-opacity duration-500',
            live && 'opacity-0',
          )}
          style={{ left: '-14.58%', top: '2.27%', width: '129.17%' }}
        />
      )}
      {children}
    </span>
  )
}
